import { z } from "zod";

// Single source of truth for analysis finding IDs.
//
// Order is alphabetical and MUST match the Go-side enum
// (`apps/worker/internal/qlik/scoring`). A parity test on the Go side asserts
// the two lists are identical.
export const ANALYSIS_FINDING_IDS = [
  "broken-dimension",
  "broken-measure",
  "circular-references",
  "high-risk-measures",
  "high-risk-objects",
  "invalid-variable",
  "synthetic-keys",
  "unused-dimensions",
  "unused-field-memory",
  "unused-measures",
  "unused-variables",
  "wasted-memory-high",
] as const;

export const analysisFindingIdSchema = z.enum(ANALYSIS_FINDING_IDS);
export type AnalysisFindingId = z.infer<typeof analysisFindingIdSchema>;

export const analysisSeveritySchema = z.enum(["critical", "high", "warning", "info"]);
export type AnalysisSeverity = z.infer<typeof analysisSeveritySchema>;

export const analysisConfidenceSchema = z.enum(["low", "medium", "high"]);
export type AnalysisConfidence = z.infer<typeof analysisConfidenceSchema>;

export const analysisCategorySchema = z.enum([
  "memory",
  "expressions",
  "usage-hygiene",
  "model-complexity",
  "object-complexity",
]);
export type AnalysisCategory = z.infer<typeof analysisCategorySchema>;

export const analysisGradeSchema = z.enum(["excellent", "good", "fair", "poor", "critical"]);
export type AnalysisGrade = z.infer<typeof analysisGradeSchema>;

// --- Expression risk (object complexity) ---
//
// Single source of truth for expression risk-factor IDs.
//
// Order is alphabetical and MUST match the Go-side enum
// (`apps/worker/internal/qlik/scoring/risk.go`, `AllRiskFactorIDs()`). A parity
// test on the Go side asserts the two lists are identical.
export const ANALYSIS_RISK_FACTOR_IDS = [
  "deep-nesting",
  "distinct-aggregation",
  "high-cardinality-field",
  "if-in-aggregation",
  "long-expression",
  "nested-aggr",
] as const;

export const analysisRiskFactorIdSchema = z.enum(ANALYSIS_RISK_FACTOR_IDS);
export type AnalysisRiskFactorId = z.infer<typeof analysisRiskFactorIdSchema>;

export const analysisRiskLevelSchema = z.enum(["none", "low", "medium", "high"]);
export type AnalysisRiskLevel = z.infer<typeof analysisRiskLevelSchema>;

// --- Diff findings ---
//
// Separate catalog: these are emitted by the on-demand diff between two
// analyses (TypeScript-only, API service). They are deliberately NOT part of
// `ANALYSIS_FINDING_IDS`, which has a parity test against the Go enum.
export const ANALYSIS_DIFF_FINDING_IDS = [
  "broken-resolved",
  "field-memory-growth",
  "health-score-drop",
  "memory-reduction",
  "new-broken-dimension",
  "new-broken-measure",
  "new-invalid-variable",
  "wasted-memory-increase",
  "wasted-memory-reduction",
] as const;

export const analysisDiffFindingIdSchema = z.enum(ANALYSIS_DIFF_FINDING_IDS);
export type AnalysisDiffFindingId = z.infer<typeof analysisDiffFindingIdSchema>;

export const analysisDiffKindSchema = z.enum(["regression", "improvement", "neutral"]);
export type AnalysisDiffKind = z.infer<typeof analysisDiffKindSchema>;

// A health-score drop is not tied to a single health category, so diff findings
// extend the shared category list with "overall".
export const analysisDiffCategorySchema = z.union([analysisCategorySchema, z.literal("overall")]);
export type AnalysisDiffCategory = z.infer<typeof analysisDiffCategorySchema>;
